import { useLayoutEffect } from 'react';

/**
 * Hook to track viewport height changes using the Visual Viewport API
 * and update a CSS custom property for use in components.
 *
 * This is particularly useful for iOS Safari where the viewport height
 * changes when the address bar shows/hides or the keyboard appears.
 *
 * Falls back to window.innerHeight for browsers without Visual Viewport API support.
 */
const useViewportHeight = () => {
  useLayoutEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;
    let lastHeight: number | null = null;

    const setHeight = () => {
      const height = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      if (height === lastHeight) return;
      lastHeight = height;

      document.documentElement.style.setProperty(
        '--viewport-height',
        `${height}px`
      );
    };

    // Debounce standard window resizes (like orientation changes).
    const onWindowResize = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(setHeight, 100);
    };

    // Use requestAnimationFrame for visual viewport changes to keep the layout
    // perfectly in sync with browser chrome transitions (address bar, keyboard).
    const onVisualViewportResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(setHeight);
    };

    setHeight();

    window.addEventListener('resize', onWindowResize, {
      passive: true,
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVisualViewportResize, {
        passive: true,
      });
      // iOS Safari (especially in Private mode) often fails to trigger 'resize'
      // immediately when the address bar toggles. Listening to 'scroll' on the
      // visual viewport ensures we catch height changes during the
      // expansion/contraction animation of the browser chrome.
      window.visualViewport.addEventListener('scroll', onVisualViewportResize, {
        passive: true,
      });
    }

    // Cleanup
    return () => {
      if (debounceId) clearTimeout(debounceId);
      if (rafId) cancelAnimationFrame(rafId);

      window.removeEventListener('resize', onWindowResize);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          onVisualViewportResize
        );
        window.visualViewport.removeEventListener(
          'scroll',
          onVisualViewportResize
        );
      }
    };
  }, []);
};

export default useViewportHeight;
